// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, AppIcon, NotificationBadge, NotificationPanel } from '@loa/ui-components';
import { useNotifications } from '../contexts/NotificationContext';
import { groupNotificationsByDate } from '../utils/notificationGrouping';
import { getNotificationRoute } from '../utils/notificationRoutes';

export function NotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen && notifications.length === 0) {
      fetchNotifications(true);
    }
  };

  const handleItemClick = async (id: string) => {
    const notification = notifications.find((n) => n.id === id);
    if (!notification) return;

    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    const route = getNotificationRoute(notification);
    if (route) {
      navigate(route);
      setIsOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/notifications');
  };

  const handleLoadMore = () => {
    fetchNotifications(false);
  };

  const groups = useMemo(
    () => groupNotificationsByDate(notifications),
    [notifications]
  );

  return (
    <div ref={popupRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <IconButton
          variant="round-filled"
          size="lg"
          icon={<AppIcon name="bell" size="large" />}
          onClick={handleBellClick}
          aria-label="Notifications"
        />
        <NotificationBadge count={unreadCount} />
      </div>

      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000, marginTop: '8px' }}>
          <NotificationPanel
            groups={groups}
            unreadCount={unreadCount}
            loading={loading}
            hasMore={hasMore}
            onItemClick={handleItemClick}
            onMarkAllRead={handleMarkAllRead}
            onViewAll={handleViewAll}
            onLoadMore={handleLoadMore}
          />
        </div>
      )}
    </div>
  );
}
